import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../client';
import { qk } from './keys';
import type {
  Booking,
  BookingDetails,
  FlightOption,
  PaginatedResponse,
  TicketClass,
  TransportKind,
} from '../types';
import { withQuery } from '@/utils/url';

export type BookingListParams = {
  status?: string;
  cursor?: string;
  limit?: number;
  [k: string]: string | number | undefined;
};

export function useBookings(params: BookingListParams = {}) {
  return useQuery({
    queryKey: qk.bookings(params),
    queryFn: () => apiGet<PaginatedResponse<Booking>>(withQuery('/bookings', params)),
  });
}

export function useBookingDetails(id: string | null) {
  return useQuery({
    queryKey: qk.bookingDetails(id ?? ''),
    queryFn: () => apiGet<BookingDetails>(`/bookings/${id ?? ''}`),
    enabled: !!id,
  });
}

export function useTriggerManualBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost<BookingDetails>('/bookings/manual', { idempotency_key: crypto.randomUUID() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
}

export interface FlightSearchInput {
  transport?: TransportKind;
  from_iata: string;
  to_iata: string;
  depart_date: string;
  return_date?: string | null;
  passengers_count: number;
  class?: TicketClass;
}

export function useSearchFlights() {
  return useMutation({
    mutationFn: (body: FlightSearchInput) =>
      apiPost<FlightOption[]>('/bookings/search-flights', body),
  });
}

export function useSelectBookingOption(bookingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (optionId: string) =>
      apiPost<BookingDetails>(`/bookings/${bookingId}/select-option`, { option_id: optionId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.bookingDetails(bookingId) });
    },
  });
}

export function usePurchaseBooking(bookingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<BookingDetails>(`/bookings/${bookingId}/purchase`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.bookingDetails(bookingId) });
      qc.invalidateQueries({ queryKey: qk.wallet });
    },
  });
}

export function useCancelBooking(bookingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<BookingDetails>(`/bookings/${bookingId}/cancel`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.bookingDetails(bookingId) });
      qc.invalidateQueries({ queryKey: qk.wallet });
    },
  });
}

export function useOrderTaxi(bookingId: string) {
  return useMutation({
    mutationFn: (body: { pickup_lat: number; pickup_lng: number; pickup_at: string }) =>
      apiPost<{ taxi_order_id: string; estimated_pickup_in_min: number }>(
        `/bookings/${bookingId}/order-taxi`,
        body,
      ),
  });
}

export function useEtkUrl(bookingId: string | null) {
  return useQuery({
    queryKey: ['bookings', bookingId, 'etk'] as const,
    queryFn: () =>
      apiGet<{ download_url: string; expires_at: string }>(`/bookings/${bookingId}/etk`),
    enabled: false, // fetched on-demand
  });
}
